import {
  AuthService,
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  PublicUserService,
  ServerService,
  toAbsoluteAvatarAssetUrl,
} from '@affine/core/modules/cloud';
import { MemberSearchService } from '@affine/core/modules/permissions';
import { CalendarMutationOutboxService } from '@affine/core/modules/streamlain/calendar';
import { onBackendAvailable } from '@affine/core/utils/backend-availability';
import { CalendarReconcileServiceExtension } from '@blocksuite/affine/data-view';
import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import { UserAvatarCacheServiceExtension } from '@blocksuite/affine/shared/services';
import { FrameworkProvider } from '@toeverything/infra';
import { z } from 'zod';

import { patchUserExtensions } from './user';
import { patchUserListExtensions } from './user-list';

const optionsSchema = z.object({
  framework: z.instanceof(FrameworkProvider).optional(),
  enableCloud: z.boolean().optional(),
});

type CloudViewOptions = z.infer<typeof optionsSchema>;

export class CloudViewExtension extends ViewExtensionProvider<CloudViewOptions> {
  override name = 'affine-view-cloud';

  override schema = optionsSchema;

  override setup(context: ViewExtensionContext, options?: CloudViewOptions) {
    super.setup(context, options);
    const framework = options?.framework;
    if (!framework) {
      return;
    }
    const calendarMutationOutbox = framework.getOptional(
      CalendarMutationOutboxService
    );
    const calendarExtensions = calendarMutationOutbox
      ? [
          CalendarReconcileServiceExtension({
            onBackendAvailable,
            submit: input => calendarMutationOutbox.submitReconcileEvent(input),
          }),
        ]
      : [];
    const publicUserService = framework.getOptional(PublicUserService);
    const authService = framework.getOptional(AuthService);
    if (!publicUserService || !authService) {
      context.register(calendarExtensions);
      return;
    }
    const avatarCache = framework.getOptional(AvatarAssetCacheService);
    const serverService = framework.getOptional(ServerService);
    const memberSearchService = framework.getOptional(MemberSearchService);
    const extensions = [
      ...calendarExtensions,
      patchUserExtensions(publicUserService, authService),
    ];
    if (avatarCache && serverService) {
      extensions.unshift(
        UserAvatarCacheServiceExtension({
          resolveCachedUserAvatarUrl: async ({ avatarUrl, userId }) => {
            const account = authService.session.account$.signal.value;
            const sourceUrl = toAbsoluteAvatarAssetUrl(
              avatarUrl,
              serverService.server.baseUrl
            );
            if (!account || !sourceUrl) {
              return null;
            }
            const input: AvatarAssetCacheInput = {
              kind: 'user-avatar',
              scope: {
                accountId: account.id,
                serverId: serverService.server.id,
              },
              entityId: userId,
              source: sourceUrl,
            };
            return avatarCache.objectUrl(input, serverService.server.baseUrl);
          },
        })
      );
    }
    if (memberSearchService) {
      const session = memberSearchService.createSession();
      extensions.unshift(...patchUserListExtensions(session));
    }
    context.register(extensions);
  }
}
